import { holidaysFor, type PublicHoliday } from "./holidays";
import { HOUR, dayKey, isSunday } from "./time";
import type { EmploymentTerms, Shift } from "./types";

export interface HourSplit {
  total: number; ordinary: number; overtime: number; sunday: number;
  /** Hours worked on a public holiday. Kept apart from `sunday` and from
   *  `ordinary` because BCEA s33(2) requires a payslip to state them, and
   *  because s18 prices the whole day rather than the hours. */
  holiday: number;
}

/**
 * What kind of day a shift fell on.
 *
 * SOUTH AFRICA ONLY, for now. Everything below is BCEA and the Public Holidays
 * Act. When Shifty sells outside South Africa this goes behind CountryPack in
 * core/countries — see the future-markets note. The statute is quoted at each
 * rule so the next country's rules can be written beside them rather than on
 * top of them.
 */
export interface DayContext {
  /** The public holiday this day is, if it is one. */
  holiday?: PublicHoliday;
  /**
   * Whether this weekday is one the employee ordinarily works, from
   * work_pattern.
   *
   * Defaults to true where it is unknown, and that direction is deliberate:
   * the "ordinarily works" branch of s18 never pays less than the other, so an
   * unknown pattern overpays rather than underpays. Paying above the BCEA
   * floor is lawful; paying below it is not, and an employer who has not
   * filled in a work pattern should not have that land on the worker.
   */
  ordinarilyWorks: boolean;
}

/**
 * A day with nothing special about it.
 *
 * Deliberately greppable: every call site still passing this is one that has
 * not been given a real calendar yet, and will price a public holiday as an
 * ordinary day. Step 4 of docs/public-holiday-pay.md removes them.
 */
export const ordinaryDay = (): DayContext => ({ ordinarilyWorks: true });

/** The break only comes off once the shift is long enough to owe one.
 *  A flat deduction robs a 3-hour garden shift of 45 minutes it never took. */
export function shiftHours(shift: Shift, terms: EmploymentTerms): number {
  if (!shift.clockOut) return 0;
  const gross = (shift.clockOut - shift.clockIn) / HOUR;
  const brk = gross >= terms.breakAfter ? terms.breakMins / 60 : 0;
  return Math.max(0, gross - brk);
}

export function splitHoursOn(shift: Shift, terms: EmploymentTerms, day: DayContext): HourSplit {
  const total = shiftHours(shift, terms);
  // A public holiday takes the whole day, including one that is also a Sunday.
  // There is no overtime bucket on a holiday: s18 prices the day, and hours
  // past the ordinary day are already inside the "amount earned" it speaks of.
  if (day.holiday) return { total, ordinary: 0, overtime: 0, sunday: 0, holiday: total };
  if (isSunday(shift.clockIn)) return { total, ordinary: 0, overtime: 0, sunday: total, holiday: 0 };
  const ordinary = Math.min(total, terms.ordinaryDaily);
  return { total, ordinary, overtime: Math.max(0, total - ordinary), sunday: 0, holiday: 0 };
}


export function summarise(
  shifts: Shift[], termsFor: (s: Shift) => EmploymentTerms, dayFor: (s: Shift) => DayContext,
) {
  return shifts.reduce(
    (acc, s) => {
      const h = splitHoursOn(s, termsFor(s), dayFor(s));
      acc.total += h.total;
      acc.ordinary += h.ordinary;
      acc.overtime += h.overtime;
      acc.sunday += h.sunday;
      acc.holiday += h.holiday;
      acc.shifts += s.clockOut ? 1 : 0;
      return acc;
    },
    { total: 0, ordinary: 0, overtime: 0, sunday: 0, holiday: 0, shifts: 0 }
  );
}

/** Price each shift against the terms in force on the day it happened, never
 *  against today's rate. This is the whole point of effective dating.
 *
 *  payForSplit is gone: a split of hours cannot say what a public holiday is
 *  worth, because s18 prices the day rather than the hours. payForDay does. */
export function payFor(shift: Shift, terms: EmploymentTerms, day: DayContext): number {
  return payForDay(shift, terms, day).amount;
}

/**
 * Build the day lookup a set of shifts needs, once.
 *
 * Holidays are computed per year and cached, so a month of shifts works the
 * calendar out once rather than thirty times.
 *
 * `worksWeekday` comes from work_pattern. Leave it out where the pattern is not
 * to hand: the default says the employee ordinarily works every day, which
 * overpays rather than underpays — see DayContext for why that direction is the
 * safe one. `declared` carries proclaimed days that no rule can produce.
 */
export function dayLookup(opts: {
  declared?: PublicHoliday[];
  worksWeekday?: (weekday: number) => boolean;
} = {}): (shift: Shift) => DayContext {
  const byYear = new Map<number, Map<string, PublicHoliday>>();
  return (shift) => {
    const iso = dayKey(shift.clockIn);
    const year = Number(iso.slice(0, 4));
    let inYear = byYear.get(year);
    if (!inYear) {
      // Undefined lets holidaysFor use the declared days the client loaded.
      inYear = holidaysFor([year], opts.declared);
      byYear.set(year, inYear);
    }
    return {
      holiday: inYear.get(iso),
      ordinarilyWorks: opts.worksWeekday
        ? opts.worksWeekday(new Date(shift.clockIn).getDay())
        : true,
    };
  };
}

/**
 * One shift's day, for a screen that is showing a single shift.
 *
 * Rebuilds the year's calendar on every call, so use dayLookup when pricing
 * more than one. No work pattern, so a public holiday counts as a day the
 * person ordinarily works — which overpays rather than underpays.
 */
export const dayOf = (shift: Shift): DayContext => dayLookup()(shift);

/* ---------- what a day is worth ---------- */

/**
 * Why the amount came out as it did.
 *
 * Carried around rather than recomputed so a payslip can state the basis and a
 * dispute can be answered without anybody deriving it again.
 */
export type PayBasis =
  | "ordinary"
  /** BCEA s16(1): double an hour, or 1.5x where the employee ordinarily works Sundays. */
  | "sunday"
  /** BCEA s16(2): a short Sunday still pays at least the ordinary daily wage. */
  | "sunday-floor"
  /** BCEA s18(2)(b)(i): at least double the ordinary daily wage. */
  | "holiday-double"
  /** BCEA s18(2)(b)(ii) and s18(3): the ordinary daily wage plus what the hours earned. */
  | "holiday-plus-earned";

export interface DayPay { amount: number; basis: PayBasis; }

/**
 * BCEA s16, Sunday work, including the floor almost everybody forgets.
 *
 *   (1) ... at double the employee's wage for each hour worked, unless the
 *       employee ordinarily works on a Sunday, in which case ... one and
 *       one-half times ...
 *   (2) If an employee works less than the employee's ordinary shift on a
 *       Sunday and the payment that the employee is entitled to in terms of
 *       subsection (1) is less than the employee's ordinary daily wage, the
 *       employer must pay the employee the employee's ordinary daily wage.
 *
 * Subsection (2) is the part Shifty had wrong: two hours on a Sunday at double
 * is four hours' pay, and the Act says a nine-hour day's wage. Half a day's
 * work on a Sunday was being underpaid by more than half.
 *
 * Note for later: s16(1)'s two rates are one number here, `terms.sunday`,
 * defaulting to 2. An employer whose worker ordinarily works Sundays must set
 * 1.5 themselves. That is fine for a household and wrong by default for a
 * seven-day restaurant — see the future-markets note.
 */
function sundayPay(hours: number, terms: EmploymentTerms): DayPay {
  const perHour = hours * terms.rate * terms.sunday;
  const ordinaryDayWage = terms.rate * terms.ordinaryDaily;
  if (hours < terms.ordinaryDaily && perHour < ordinaryDayWage) {
    return { amount: ordinaryDayWage, basis: "sunday-floor" };
  }
  return { amount: perHour, basis: "sunday" };
}

/**
 * What one shift is worth, given what kind of day it fell on.
 *
 * BCEA s18, quoted, because the shape of it is not obvious:
 *
 *   (2) If a public holiday falls on a day on which an employee would
 *       ordinarily work, an employer must pay —
 *       (a) an employee who does not work ... at least the wage that the
 *           employee would ordinarily have received for work on that day;
 *       (b) an employee who does work on the public holiday —
 *           (i)  at least double the amount referred to in paragraph (a); or
 *           (ii) if it is greater, the amount referred to in paragraph (a)
 *                plus the amount earned ... for the time worked on that day.
 *   (3) If an employee works on a public holiday on which the employee would
 *       not ordinarily work ... the employee's ordinary daily wage; plus the
 *       amount earned ... for the work performed that day.
 *
 * So it is a WHOLE-DAY amount, never an hourly multiplier. Four hours of a
 * nine-hour day still owes two days' wage; twelve hours owes a day plus twelve
 * hours. That is why this cannot live in payForSplit.
 *
 * A public holiday that falls on a Sunday pays the greater of s16 and s18.
 * Neither section disapplies the other and both are worded as floors — "must
 * pay ... at least" — so an employer has to satisfy both. It matters in both
 * directions: s18 wins on a short day, s16 wins once the hours run past the
 * ordinary day. Randfontein Estates v NUM is about the Sunday AND the Monday
 * that follows both being public holidays, which is a different question and
 * is handled in core/holidays.
 *
 * Overtime does not stack on top of either. The Act prices the day.
 */
export function payForDay(shift: Shift, terms: EmploymentTerms, day: DayContext): DayPay {
  if (!shift.clockOut) return { amount: 0, basis: "ordinary" };

  const h = splitHoursOn(shift, terms, day);
  const ordinaryDayWage = terms.rate * terms.ordinaryDaily;   // the Act's "wage ... for that day"
  const earned = h.total * terms.rate;                        // "the amount earned ... for the time worked"
  const onSunday = isSunday(shift.clockIn);

  if (day.holiday) {
    const doubled = ordinaryDayWage * terms.holiday;
    const plusEarned = ordinaryDayWage + earned;
    const takeDouble = day.ordinarilyWorks && doubled >= plusEarned;
    const holidayPay: DayPay = day.ordinarilyWorks
      ? { amount: Math.max(doubled, plusEarned), basis: takeDouble ? "holiday-double" : "holiday-plus-earned" }
      : { amount: plusEarned, basis: "holiday-plus-earned" };

    if (!onSunday) return holidayPay;
    const s16 = sundayPay(h.total, terms);
    return s16.amount > holidayPay.amount ? s16 : holidayPay;
  }

  if (onSunday) return sundayPay(h.total, terms);

  return { amount: h.ordinary * terms.rate + h.overtime * terms.rate * terms.ot, basis: "ordinary" };
}

/* ---------- overtime awaiting a decision ---------- */

/** Overtime that nobody has answered yet. Sunday hours are not overtime and
 *  are never held back — a Sunday is paid at its multiplier because it is a
 *  Sunday, not because anybody worked late. */
export function pendingOvertimeHours(
  shift: Shift, terms: EmploymentTerms, day: DayContext,
): number {
  if (!shift.clockOut) return 0;
  if (shift.overtimeStatus) return 0;           // decided, either way
  // A public holiday has no overtime bucket — s18 prices the whole day, so
  // there is nothing for an employer to approve or decline.
  return splitHoursOn(shift, terms, day).overtime;
}

export interface PayBreakdown {
  /** Accepted into the total: ordinary, Sunday, and overtime already decided. */
  settled: number;
  /** Worked, owed, and still waiting on the employer. Never hidden, never
   *  silently dropped — see 0022 for why the BCEA leaves no room for that. */
  awaiting: number;
}

/**
 * Pay split into what has been signed off and what is still a question.
 *
 * Declined overtime stays in `settled`. That is deliberate and it is the whole
 * point: declining records that the employer did not sanction the extra hours,
 * which is a matter for a conversation. It does not mean the hours were not
 * worked, and the BCEA does not let an employer answer worked hours with
 * nothing. See supabase/migrations/0022.
 */
export function payBreakdown(
  shift: Shift, terms: EmploymentTerms, day: DayContext,
): PayBreakdown {
  const pending = pendingOvertimeHours(shift, terms, day);
  const otRate = terms.rate * terms.ot;
  // payForDay knows every rule that prices a day — Sunday floors, the two
  // halves of s18, and which of them wins. Only the overtime still waiting on
  // an answer is held back out of it.
  return {
    settled: payForDay(shift, terms, day).amount - pending * otRate,
    awaiting: pending * otRate,
  };
}

export function summarisePay(
  shifts: Shift[], termsFor: (s: Shift) => EmploymentTerms, dayFor: (s: Shift) => DayContext,
): PayBreakdown & { pendingHours: number; pendingShifts: number } {
  return shifts.reduce(
    (acc, s) => {
      const t = termsFor(s);
      const d = dayFor(s);
      const b = payBreakdown(s, t, d);
      const ph = pendingOvertimeHours(s, t, d);
      acc.settled += b.settled;
      acc.awaiting += b.awaiting;
      acc.pendingHours += ph;
      acc.pendingShifts += ph > 0 ? 1 : 0;
      return acc;
    },
    { settled: 0, awaiting: 0, pendingHours: 0, pendingShifts: 0 },
  );
}

/**
 * The terms in force when a shift started.
 *
 * The day is read in local time. It was read in UTC, which put a shift that
 * began between midnight and 02:00 in Johannesburg on the previous day — so a
 * night worker's first shift under a raise was priced at the old rate.
 */
export function termsAt(all: EmploymentTerms[], at: number): EmploymentTerms | undefined {
  const day = dayKey(at);
  return all.find((t) => t.validFrom <= day && (t.validTo === null || t.validTo >= day));
}
